import { getTaxRateByRegion } from '@principle-theorem/accounting';
import {
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Brand,
  TreatmentConfiguration,
} from '@principle-theorem/principle-core';
import {
  TreatmentConfigurationName,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  getError,
  snapshotCombineLatest,
  where,
  type WithRef,
} from '@principle-theorem/shared';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientTreatmentPlanDestinationEntity,
  IPatientTreatmentPlanJobData,
  IPatientTreatmentPlanMigrationData,
  PATIENT_TREATMENT_PLAN_DESTINATION_ENTITY,
} from '../../../destination/entities/patient-treatment-plans';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { AppointmentSourceEntity } from '../../../exact/source/entities/patient-appointment';
import {
  ExactTreatment,
  ExactTreatmentType,
  PatientTreatmentSourceEntity,
} from '../../../exact/source/entities/patient-treatments';
import { ExactFeeScheduleMappingHandler } from '../../../exact/destination/mappings/fee-schedules';
import { ExactItemCodeToTreatmentMappingHandler } from '../../../exact/destination/mappings/item-code-to-treatment';
import { ExactItemCodeMappingHandler } from '../../../exact/destination/mappings/item-codes';
import { StafferDestinationEntity } from './staff';
import { ExactStafferMappingHandler } from '../../../exact/destination/mappings/staff';
import { PracticeWorksTreatmentPlanBuilder } from './lib/practice-works-treatment-plan-builder';
import {
  PatientSourceEntity,
  PracticeWorksPatient,
} from '../../source/entities/patients';
import { PatientDestinationEntity } from './patient';

const patientTreatmentPlanDestinationEntity =
  DestinationEntity.withMetadataDescription(
    PATIENT_TREATMENT_PLAN_DESTINATION_ENTITY,
    `This migrates the treatments for each patient. Exact does have a loose structure for treatment plans, so we are going to migrate in similar fashion.
      - Treatments with a plan id will be grouped into a treatment plan using the Id (as they do in Exact) - a description is also used for naming if present
      - Treatments with a visit_id will be grouped into steps under the plans and the visit_sequence will determine the order of the steps
      - Treatments without a plan id will be placed in a default plan with one step for all completed and one for incomplete

      Treatments all have a type in Exact:
      - 'Historic' treatments will be considered completed
      - 'Base' treatments are considered existing so should be treated like conditions?
      - 'Planned' are considered incomplete IF they have no appointment_id or the appointment is not complete
    `
  );

type JobData = IPatientTreatmentPlanJobData<PracticeWorksPatient>;

export class PatientTreatmentPlanDestinationEntity extends BasePatientTreatmentPlanDestinationEntity<
  PracticeWorksPatient,
  JobData
> {
  override destinationEntity = patientTreatmentPlanDestinationEntity;
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
    appointments: new AppointmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
    staff: new StafferDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    feeSchedules: new ExactFeeScheduleMappingHandler(),
    itemCodes: new ExactItemCodeMappingHandler(),
    itemCodeToTreatment: new ExactItemCodeToTreatmentMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<JobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const brand$ = PracticeMigration.brand$(migration);
    const practitioners$ = of([]);
    const taxRate$ = PracticeMigration.organisation$(migration).pipe(
      map((org) => getTaxRateByRegion(org.region))
    );
    const defaultTreatmentConfiguration$ = brand$.pipe(
      switchMap((brand) =>
        Firestore.firstResult(
          TreatmentConfiguration.col(brand),
          where('name', '==', TreatmentConfigurationName.GeneralADACode)
        )
      ),
      map((config) => {
        if (!config) {
          throw new Error(
            `Couldn't find default treatment configuration ${TreatmentConfigurationName.GeneralADACode}`
          );
        }
        return config;
      })
    );
    const treatmentCategories$ = brand$.pipe(
      switchMap((brand) => Brand.treatmentCategories$(brand))
    );
    const treatmentConfigurations$ = brand$.pipe(
      switchMap((brand) => TreatmentConfiguration.all$(brand))
    );

    return snapshotCombineLatest([
      staff$,
      practitioners$,
      defaultTreatmentConfiguration$,
      treatmentConfigurations$,
      this.customMappings.itemCodeToTreatment.getRecords(translationMap),
      this.customMappings.itemCodes.getRecords(translationMap),
      treatmentCategories$,
      taxRate$,
    ]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(
        ([
          sourcePatients,
          [
            staff,
            practitioners,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            treatmentConfigurationMappings,
            sourceItemCodes,
            treatmentCategories,
            taxRate,
          ],
        ]) =>
          sourcePatients.map((sourcePatient) => ({
            sourcePatient,
            staff,
            practitioners,
            defaultTreatmentConfiguration,
            treatmentConfigurations,
            treatmentConfigurationMappings,
            sourceItemCodes,
            treatmentCategories,
            taxRate,
          }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: JobData
  ): Promise<
    | IPatientTreatmentPlanMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = this.sourceEntities.patients.getSourceRecordId(
      data.sourcePatient.data
    );

    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(data.sourcePatient, 'No patient');
    }

    const treatments = await getExactPatientTreatments(
      this.sourceEntities.treatments,
      migration,
      sourcePatientId
    );

    const appointments = await this.sourceEntities.appointments.filterRecords(
      migration,
      'patientId',
      sourcePatientId
    );

    try {
      const planPairs =
        await PracticeWorksTreatmentPlanBuilder.buildTreatmentPlanData(
          data,
          translationMap,
          treatments,
          migration,
          sourcePatientId,
          appointments,
          this.sourceEntities.appointments
        );
      return {
        sourcePatientId,
        patientRef,
        planPairs,
      };
    } catch (error) {
      return this._buildErrorResponse(data.sourcePatient, getError(error));
    }
  }
}

export async function getExactPatientTreatments(
  treatmentSourceEntity: PatientTreatmentSourceEntity,
  migration: WithRef<IPracticeMigration>,
  sourcePatientId: string
): Promise<IGetRecordResponse<ExactTreatment>[]> {
  return treatmentSourceEntity.filterRecords(
    migration,
    'patientId',
    sourcePatientId,
    (record) => record.data.treatment_type !== ExactTreatmentType.Base
  );
}
